/**
 * Investor organization data builder utilities
 * Constructs complex organization mutation objects for create/update operations
 * Eliminates ~140 lines of duplicate mutation data
 */

import { Bank } from "../../../types/org.type";
import { DocumentsUpload } from "@funded-here-interface/common/src/constant/type";
import {
  CompanyRole,
  InvestmentMode,
  KYCStatus,
  Persona,
} from "@funded-here-interface/common/src/constant/enum";
import { Industry, LegalStructure } from "../../../constants/enums";

/**
 * Bank state from Redux
 */
export interface InvestorBankState {
  bankName: string | null;
  bankCode: string | null;
  swiftCode: string | null;
  accountHolderName: string | null;
  accountNumber: string | null;
  accountType: string | null;
  bankAddress: string | null;
  bankCountry: string | null;
  currency: string | null;
}

/**
 * Business state from Redux
 */
export interface InvestorBizState {
  company: string | null;
  regNumber: string | null;
  legalStructure: string | null;
  legalStructureOthers: string | null;
  countryOfDomicile: string | null;
  countryOfIncorp: string | null;
  region: string | null;
  city: string | null;
  address: string | null;
  postal: string | null;
  phoneNumber: string | null;
  website: string | null;
  industryGroup: string | null;
  industry: string | null;
  industryOthers: string | null;
  investmentAmount: number | null;
  incorporationDate: string | null;
}

/**
 * Representative state from Redux
 */
export interface InvestorRepState {
  repFirstName: string | null;
  repLastName: string | null;
  repTitle: string | null;
  repEmail: string | null;
  userTitle: string | null;
  shareholdersDirectors: ShareholderDirector[] | null;
}

/**
 * Shareholder/Director type
 */
export interface ShareholderDirector {
  firstName: string;
  lastName: string;
  email: string;
  role: CompanyRole;
}

/**
 * Build Bank object from Redux state
 */
export const buildInvestorBankObject = (bankState: InvestorBankState): Bank => {
  return {
    bankName: bankState.bankName ?? "",
    bankCode: bankState.bankCode || "",
    swiftCode: bankState.swiftCode ?? "",
    accountNo: bankState.accountNumber ?? "",
    accountHolderName: bankState.accountHolderName ?? "",
    accountType: (bankState.accountType ?? "").toLowerCase(),
    bankAddress: bankState.bankAddress ?? "",
    bankCountry: bankState.bankCountry ?? "",
    currency: bankState.currency ?? "",
  };
};

/**
 * Build investment preferences object
 */
export const buildInvestmentPreferences = (
  type: string,
  investmentAmount: number | null
) => {
  if (type === Persona.INVESTOR && investmentAmount) {
    return {
      investmentSize: investmentAmount,
      investmentMode: InvestmentMode.MANUAL,
    };
  }
  return null;
};

/**
 * Map shareholders from shareholdersDirectors
 */
export const mapShareholders = (
  shareholdersDirectors: ShareholderDirector[] | null
) => {
  if (!shareholdersDirectors) return null;

  const shareholders = shareholdersDirectors.filter(
    (user) => user.role === CompanyRole.SHAREHOLDER
  );

  if (shareholders.length === 0) return null;

  return shareholders.map((shareholder) => ({
    firstName: shareholder.firstName,
    lastName: shareholder.lastName,
    email: shareholder.email.toLowerCase(),
  }));
};

/**
 * Map directors from shareholdersDirectors
 */
export const mapDirectors = (
  shareholdersDirectors: ShareholderDirector[] | null,
  repEmail: string | null
) => {
  if (!shareholdersDirectors) return null;

  const directors = shareholdersDirectors.filter(
    (user) =>
      user.role === CompanyRole.BOTH || user.role === CompanyRole.DIRECTOR
  );

  if (directors.length === 0) return null;

  return directors.map((director) => ({
    firstName: director.firstName,
    lastName: director.lastName,
    email: director.email.toLowerCase(),
    isShareholder: director.role === CompanyRole.BOTH,
    isAuthRep: director.email.toLowerCase() === repEmail?.toLowerCase(),
  }));
};

/**
 * Base organization data shared between create and update
 */
export interface BaseInvestorOrgData {
  bank: Bank;
  token: string;
  onBoardingUserType: string;
  nameOfAuthorisedRepresentiative: string;
  emailOfAuthorisedRepresentiative: string;
  jobTitle: string | null;
  legalEntityAddress: string;
  countryISOCode: string | null;
  region: string | null;
  city: string | null;
  postalCode: string;
  documentsUpload: DocumentsUpload;
  investmentPreferences: {
    investmentSize: number;
    investmentMode: string;
  } | null;
  companyName: string;
  companyRegistrationNo: string;
  companyPhoneNumber: string;
  titleOfAuthorisedRepresentiative: string;
  countryOfIncorporation: string;
  countryOfDomicile: string;
  website: string;
  industryGroup: string;
  industry: string;
  legalStructure: string;
  shareHolders: { firstName: string; lastName: string; email: string }[] | null;
  boardOfDirectors:
    | {
        firstName: string;
        lastName: string;
        email: string;
        isShareholder: boolean;
        isAuthRep: boolean;
      }[]
    | null;
  companyStartDate: string | null;
}

/**
 * Build base organization data object
 */
export const buildBaseInvestorOrgData = (params: {
  bizState: InvestorBizState;
  repState: InvestorRepState;
  bank: Bank;
  token: string;
  onBoardingUserType: string;
  countryCode: string | null;
  documentsUpload: DocumentsUpload;
  investmentPreferences: {
    investmentSize: number;
    investmentMode: string;
  } | null;
}): BaseInvestorOrgData => {
  const {
    bizState,
    repState,
    bank,
    token,
    onBoardingUserType,
    countryCode,
    documentsUpload,
    investmentPreferences,
  } = params;

  return {
    bank,
    token,
    onBoardingUserType,
    nameOfAuthorisedRepresentiative: `${repState.repFirstName ?? ""} ${repState.repLastName ?? ""}`.trim(),
    emailOfAuthorisedRepresentiative: repState.repEmail?.toLowerCase() || "",
    jobTitle: repState.userTitle,
    legalEntityAddress: bizState.address ?? "",
    countryISOCode: countryCode,
    region: bizState.region,
    city: bizState.city,
    postalCode: bizState.postal ?? "",
    documentsUpload,
    investmentPreferences,
    companyName: bizState.company ?? "",
    companyRegistrationNo: bizState.regNumber ?? "",
    companyPhoneNumber: bizState.phoneNumber ?? "",
    titleOfAuthorisedRepresentiative: repState.repTitle ?? "",
    countryOfIncorporation: bizState.countryOfIncorp ?? "",
    countryOfDomicile: bizState.countryOfDomicile ?? "",
    website: bizState.website ?? "",
    industryGroup: bizState.industryGroup ?? "",
    industry:
      bizState.industry === Industry.OTHERS
        ? (bizState.industryOthers ?? "")
        : (bizState.industry ?? ""),
    legalStructure:
      bizState.legalStructure === LegalStructure.OTHERS
        ? (bizState.legalStructureOthers ?? "")
        : (bizState.legalStructure ?? ""),
    shareHolders: mapShareholders(repState.shareholdersDirectors),
    boardOfDirectors: mapDirectors(
      repState.shareholdersDirectors,
      repState.repEmail
    ),
    companyStartDate: bizState.incorporationDate,
  };
};

/**
 * Build create organization data
 */
export const buildCreateInvestorOrgData = (params: {
  bizState: InvestorBizState;
  repState: InvestorRepState;
  bank: Bank;
  token: string;
  onBoardingUserType: string;
  countryCode: string | null;
  documentsUpload: DocumentsUpload;
  investmentPreferences: {
    investmentSize: number;
    investmentMode: string;
  } | null;
}) => {
  return buildBaseInvestorOrgData(params);
};

/**
 * Build update organization data
 */
export const buildUpdateInvestorOrgData = (params: {
  bizState: InvestorBizState;
  repState: InvestorRepState;
  bank: Bank;
  token: string;
  onBoardingUserType: string;
  countryCode: string | null;
  documentsUpload: DocumentsUpload;
  investmentPreferences: {
    investmentSize: number;
    investmentMode: string;
  } | null;
}) => {
  return {
    ...buildBaseInvestorOrgData(params),
    kycStatus: KYCStatus.PENDING_APPROVAL, // Update includes KYC status reset
  };
};
